<template>
  <div class="market-page">
    <div class="market-bg"></div>

    <header class="market-header">
      <div>
        <p class="eyebrow">TASK MARKET</p>
        <h1>任务市场</h1>
        <p>{{ isAdmin ? 'DeRAS会先自动校验上门服务任务，管理员保留人工复核与兜底通过。' : '查看已通过任务，先确认详情和距离，再决定是否接单。' }}</p>
      </div>
      <div class="header-actions">
        <button class="ghost-btn" @click="$router.push({ name: isAdmin ? 'adminTaskMarket' : 'home' })">{{ isAdmin ? '任务审核后台' : '返回首页' }}</button>
        <button v-if="!isAdmin" class="primary-btn" @click="$router.push({ name: 'mall' })">发布任务</button>
      </div>
    </header>

    <section class="toolbar glass-card">
      <div v-if="isAdmin" class="status-tabs" role="tablist" aria-label="任务状态筛选">
        <button
          v-for="item in statusOptions"
          :key="item.value"
          type="button"
          :class="{ active: status === item.value }"
          @click="changeStatus(item.value)"
        >
          {{ item.label }}
        </button>
      </div>

      <el-select v-model="robotType" clearable placeholder="按机器人类型筛选" @change="fetchTasks">
        <el-option
          v-for="item in robotTypes"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
    </section>

    <section v-if="isAdmin" class="deras-panel glass-card">
      <div>
        <p class="eyebrow">DERAS AUDIT</p>
        <h2>DeRAS自动审核设置</h2>
        <p>当前先用于上门服务：校验发布者信誉、平台补助、联系方式和服务地点真实性；未通过或不确定的任务会进入人工复核。</p>
      </div>
      <div class="deras-controls">
        <label>
          最低信誉分
          <el-input-number
            v-model="derasSettings.minReputationScore"
            :min="0"
            :max="100"
            :step="5"
            controls-position="right"
          ></el-input-number>
        </label>
        <label>
          地点误差阈值
          <el-input-number
            v-model="derasSettings.locationMaxDistanceMeters"
            :min="50"
            :step="50"
            controls-position="right"
          ></el-input-number>
        </label>
        <label>
          并发候选数
          <el-input-number
            v-model="derasSettings.dispatchParallelLimit"
            :min="1"
            :max="5"
            :step="1"
            controls-position="right"
          ></el-input-number>
        </label>
        <label>
          邀请超时秒数
          <el-input-number
            v-model="derasSettings.dispatchInviteTimeoutSeconds"
            :min="30"
            :max="1800"
            :step="30"
            controls-position="right"
          ></el-input-number>
        </label>
        <label class="switch-row">
          上门自动审核
          <el-switch
            v-model="derasSettings.homeServiceAutoAuditEnabled"
            :active-value="1"
            :inactive-value="0"
          ></el-switch>
        </label>
        <button class="primary-btn" :disabled="settingsSaving" @click="saveSettings">
          {{ settingsSaving ? '保存中...' : '保存设置' }}
        </button>
      </div>
    </section>

    <section v-loading="loading" class="task-grid">
      <article
        v-for="task in tasks"
        :key="task.id"
        :class="['task-card', 'glass-card', { 'task-card-taken': isTakenTask(task) }]"
      >
        <div v-if="isTakenTask(task)" class="taken-stamp">已被接单</div>
        <div class="card-top">
          <div class="pill-row">
            <span class="type-pill">{{ robotTypeLabel(task.expectedRobotType) }}</span>
            <span v-if="isMyTask(task)" class="mine-pill">我的</span>
          </div>
          <span class="status-pill">{{ taskStatusLabel(task) }}</span>
        </div>
      <div class="dispatch-banner" :class="dispatchBannerClass(task)">
          <strong>{{ dispatchStatusText(task) }}</strong>
          <span>{{ dispatchSubText(task) }}</span>
          <em v-if="task.dispatchCandidateSummary">{{ task.dispatchCandidateSummary }}</em>
        </div>
        <div v-if="isMyTask(task)" class="publisher-progress">
          <div class="publisher-progress-head">
            <strong>我的派单进度</strong>
            <button type="button" @click.stop="openDetail(task)">查看时间线</button>
          </div>
          <div class="publisher-progress-steps">
            <span
              v-for="step in publisherSteps(task)"
              :key="`${task.id}-${step.key}`"
              :class="{ active: step.active, done: step.done, warn: step.warn }"
            >{{ step.label }}</span>
          </div>
          <p v-if="dispatchFallbackHint(task)" class="fallback-hint">{{ dispatchFallbackHint(task) }}</p>
        </div>
        <h2>{{ task.title || task.taskName || '未命名任务' }}</h2>
        <p class="desc">{{ task.message || '暂无任务描述' }}</p>
        <div class="meta-list">
          <span>{{ serviceTypeLabel(task.serviceType) }}</span>
          <span>预计到手 {{ formatCoin(providerIncome(task)) }} RoboCoin</span>
          <span>{{ task.contactPhone || '未填写电话' }}</span>
          <span>{{ formatAddress(task) }}</span>
        </div>
        <div class="card-actions">
          <button class="mini-btn info" @click="openDetail(task)">查看详情</button>
          <button v-if="!isAdmin && !isTakenTask(task)" class="mini-btn approve" @click="openDetail(task, true)">接单前查看</button>
          <button v-if="!isAdmin && isTakenTask(task)" class="mini-btn locked" disabled>已被接单</button>
          <button v-if="isAdmin && status === 'reviewing'" class="mini-btn approve" @click="approve(task)">人工通过</button>
          <button v-if="isAdmin && status === 'reviewing'" class="mini-btn danger" @click="reject(task)">驳回复核</button>
          <button v-if="isAdmin && status === 'approved'" class="mini-btn danger" @click="close(task)">关闭任务</button>
        </div>
      </article>

      <el-empty v-if="!loading && tasks.length === 0" description="暂无任务"></el-empty>
    </section>

    <div
      v-if="detailVisible"
      class="market-modal-backdrop"
      @click.self="closeDetail"
    >
      <section class="market-modal-panel">
        <button class="modal-close-btn" type="button" @click="closeDetail">关闭</button>
        <div v-if="selectedTask" class="detail-panel">
          <p class="eyebrow">TASK DETAIL</p>
          <h2>{{ selectedTask.title || selectedTask.taskName || '未命名任务' }}</h2>
          <p class="detail-desc">{{ selectedTask.message || '暂无任务描述' }}</p>

          <div class="detail-grid">
            <div>
              <span>机器人类型</span>
              <strong>{{ robotTypeLabel(selectedTask.expectedRobotType) }}</strong>
            </div>
            <div>
              <span>服务类型</span>
              <strong>{{ serviceTypeLabel(selectedTask.serviceType) }}</strong>
            </div>
            <div>
              <span>任务分包属性</span>
              <strong>{{ Number(selectedTask.allowSubcontract) === 1 ? '允许接单时确定两段协作' : '禁止分包' }}</strong>
            </div>
            <div>
              <span>联系电话</span>
              <strong>{{ selectedTask.contactPhone || '未填写' }}</strong>
            </div>
            <div>
              <span>目标地址</span>
              <strong>{{ targetAddress(selectedTask) }}</strong>
            </div>
            <div>
              <span>服务方预计收入</span>
              <strong>{{ formatCoin(providerIncome(selectedTask)) }} RoboCoin</strong>
            </div>
            <div v-if="isAdmin">
              <span>平台补助</span>
              <strong>{{ formatCoin(selectedTask.estimatedRobocoin) }} RoboCoin</strong>
            </div>
            <div v-if="isAdmin">
              <span>平台手续费</span>
              <strong>{{ formatCoin(platformFee(selectedTask)) }} RoboCoin</strong>
            </div>
            <div v-if="isAdmin">
              <span>手续费规则</span>
              <strong>按白皮书标准转账费 1% 计入平台/AI-UBI 费用池</strong>
            </div>
            <div>
              <span>审核状态</span>
              <strong>{{ taskStatusLabel(selectedTask) }}</strong>
            </div>
            <div>
              <span>审核说明</span>
              <strong>{{ selectedTask.verificationNote || '暂无说明' }}</strong>
            </div>
            <div>
              <span>DeRAS派单进度</span>
              <strong>{{ dispatchStatusText(selectedTask) }}</strong>
            </div>
            <div>
              <span>推荐原因</span>
              <strong>{{ selectedTask.dispatchReason || dispatchSubText(selectedTask) }}</strong>
            </div>
            <div>
              <span>候选队列</span>
              <strong>{{ selectedTask.dispatchCandidateSummary || '暂无候选队列记录' }}</strong>
            </div>
            <div>
              <span>派单兜底</span>
              <strong>{{ dispatchFallbackHint(selectedTask) || 'DeRAS仍在正常寻找候选机器人。' }}</strong>
            </div>
          </div>

          <section v-if="!isAdmin && Number(selectedTask.allowSubcontract) === 1" v-loading="subcontractCandidatesLoading" class="subcontract-planner">
            <div class="planner-heading">
              <div>
                <span>FULFILLMENT PLAN</span>
                <strong>接单时锁定执行方案</strong>
              </div>
              <div class="mode-switch">
                <button type="button" :class="{ active: acceptancePlan.executionMode === 'DIRECT' }" @click="setExecutionMode('DIRECT')">整单执行</button>
                <button type="button" :class="{ active: acceptancePlan.executionMode === 'SUBCONTRACT' }" @click="setExecutionMode('SUBCONTRACT')">两段协作</button>
              </div>
            </div>

            <div class="planner-fields">
              <label>
                <span>{{ acceptancePlan.executionMode === 'SUBCONTRACT' ? '第一段机器人' : '执行机器人' }}</span>
                <el-select v-model="acceptancePlan.primaryRobotId" placeholder="选择我的在线机器人" @change="ensureSecondaryRobot">
                  <el-option v-for="robot in subcontractCandidates.primaryRobots" :key="robot.id" :label="robotOptionLabel(robot)" :value="robot.id" />
                </el-select>
              </label>
              <label v-if="acceptancePlan.executionMode === 'SUBCONTRACT'">
                <span>第二段机器人</span>
                <el-select v-model="acceptancePlan.secondaryRobotId" filterable placeholder="选择已审核且在线的协作机器人">
                  <el-option v-for="robot in availableSecondaryRobots" :key="robot.id" :label="robotOptionLabel(robot)" :value="robot.id" />
                </el-select>
              </label>
              <label v-if="acceptancePlan.executionMode === 'SUBCONTRACT'" class="wide-field">
                <span>交接地点</span>
                <el-input v-model.trim="acceptancePlan.handoverAddress" maxlength="255" placeholder="交接点名称或详细地址" />
              </label>
              <label v-if="acceptancePlan.executionMode === 'SUBCONTRACT'">
                <span>交接纬度</span>
                <input
                  class="coordinate-input"
                  type="number"
                  min="-90"
                  max="90"
                  step="0.0000001"
                  :value="acceptancePlan.handoverLat"
                  @input="acceptancePlan.handoverLat = $event.target.value === '' ? null : Number($event.target.value)"
                />
              </label>
              <label v-if="acceptancePlan.executionMode === 'SUBCONTRACT'">
                <span>交接经度</span>
                <input
                  class="coordinate-input"
                  type="number"
                  min="-180"
                  max="180"
                  step="0.0000001"
                  :value="acceptancePlan.handoverLng"
                  @input="acceptancePlan.handoverLng = $event.target.value === '' ? null : Number($event.target.value)"
                />
              </label>
              <label v-if="acceptancePlan.executionMode === 'SUBCONTRACT'" class="wide-field reward-field">
                <span>第一段分账 {{ acceptancePlan.primaryRewardPercent }}% / 第二段 {{ 100 - acceptancePlan.primaryRewardPercent }}%</span>
                <input
                  class="reward-slider"
                  type="range"
                  min="10"
                  max="90"
                  step="5"
                  :value="acceptancePlan.primaryRewardPercent"
                  aria-label="第一段分账比例"
                  @input="acceptancePlan.primaryRewardPercent = Number($event.target.value)"
                />
              </label>
            </div>
            <p v-if="acceptancePlan.executionMode === 'SUBCONTRACT'" class="planner-note">第二段机器人确认前不能开始履约；确认后机器人、交接点和分账比例都不能中途修改。</p>
          </section>

          <div v-if="isMyTask(selectedTask)" v-loading="dispatchProgressLoading" class="publisher-timeline">
            <div class="timeline-head">
              <strong>发布者派单时间线</strong>
              <button type="button" @click="fetchDispatchProgress(selectedTask)">刷新时间线</button>
            </div>
            <div class="timeline-summary" v-if="dispatchProgress && dispatchProgress.summary">
              <span>{{ dispatchProgress.summary.text || dispatchStatusText(selectedTask) }}</span>
              <em>{{ dispatchProgress.summary.candidateSummary || selectedTask.dispatchCandidateSummary || '暂无候选队列' }}</em>
            </div>
            <div
              v-for="item in normalizedTimeline"
              :key="`${item.type}-${item.time}-${item.title}`"
              class="timeline-row"
            >
              <i :class="timelineTone(item)"></i>
              <div>
                <strong>{{ timelineTitle(item) }}</strong>
                <p>{{ timelineContent(item) }}</p>
                <small>{{ formatTime(item.time) }}</small>
              </div>
            </div>
            <el-empty v-if="!dispatchProgressLoading && normalizedTimeline.length === 0" description="暂无派单时间线"></el-empty>
          </div>

          <div class="distance-box">
            <div>
              <span>当前位置到任务起点/服务点距离</span>
              <strong>{{ distanceText }}</strong>
              <p>{{ locationHint }}</p>
            </div>
            <button class="primary-btn locate-btn" :disabled="locating || !hasTargetCoordinate(selectedTask)" @click="calculateDistance">
              {{ locating ? '定位中...' : '获取当前位置并计算距离' }}
            </button>
          </div>

          <div v-if="!isAdmin && locationPermission !== 'granted'" class="location-permission-notice" role="status">
            <strong>{{ locationPermission === 'denied' ? '定位权限尚未开启' : '允许定位后可核对接单距离' }}</strong>
            <span>{{ locationPermission === 'denied' ? '请在浏览器地址栏的网站权限中允许访问位置，然后重新获取。' : '点击获取位置时，浏览器会询问是否允许 RoboCoin 使用你的位置。' }}</span>
            <button type="button" :disabled="locating" @click="calculateDistance">{{ locationPermission === 'denied' ? '重新尝试' : '开启并获取位置' }}</button>
          </div>

          <div class="dialog-actions">
            <button class="ghost-btn" @click="closeDetail">先不接</button>
            <button v-if="!isAdmin" class="primary-btn" :disabled="accepting || isTakenTask(selectedTask)" @click="acceptTask">
              {{ isTakenTask(selectedTask) ? '已被接单' : (accepting ? 'DeRAS审批中...' : '确认接单') }}
            </button>
            <button v-if="isAdmin && status === 'reviewing'" class="primary-btn" @click="approve(selectedTask)">人工通过</button>
            <button v-if="isAdmin && status === 'reviewing'" class="mini-btn danger" @click="reject(selectedTask)">驳回复核</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  acceptMarketTask,
  approveTask,
  closeTask,
  getAdminTaskMarket,
  getDerasSettings,
  getPublishedTaskDispatchProgress,
  getSubcontractCandidates,
  getUserTaskMarket,
  rejectTask,
  saveDerasSettings
} from '../api/market'
import { getAdminToken, getAuthUser } from '../utils/auth'
import { getApiErrorMessage } from '../api/http'

export default {
  name: 'TaskMarket',
  data() {
    return {
      loading: false,
      locating: false,
      accepting: false,
      detailVisible: false,
      selectedTask: null,
      dispatchProgressLoading: false,
      dispatchProgress: null,
      userPosition: null,
      distanceMeters: null,
      locationError: '',
      locationPermission: 'unknown',
      settingsSaving: false,
      subcontractCandidatesLoading: false,
      subcontractCandidates: {
        primaryRobots: [],
        secondaryRobots: []
      },
      acceptancePlan: {
        executionMode: 'DIRECT',
        primaryRobotId: null,
        secondaryRobotId: null,
        handoverAddress: '',
        handoverLat: null,
        handoverLng: null,
        primaryRewardPercent: 50
      },
      currentUser: getAuthUser(),
      tasks: [],
      status: 'reviewing',
      robotType: '',
      derasSettings: {
        minReputationScore: 60,
        homeServiceAutoAuditEnabled: 1,
        locationMaxDistanceMeters: 1000,
        dispatchParallelLimit: 3,
        dispatchInviteTimeoutSeconds: 300
      },
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人' },
        { value: 'DELIVERY', label: '配送机器人' },
        { value: 'SHUTTLE', label: '接驳机器人' },
        { value: 'INSPECTION', label: '巡检机器人' },
        { value: 'GO2', label: '机器狗 Go2' }
      ],
      statusOptions: [
        { value: 'reviewing', label: '审核中' },
        { value: 'approved', label: '已通过' },
        { value: 'rejected', label: '未通过' }
      ]
    }
  },
  computed: {
    isAdmin() {
      return Boolean(getAdminToken())
    },
    normalizedTimeline() {
      if (!this.dispatchProgress || !Array.isArray(this.dispatchProgress.timeline)) {
        return []
      }
      return this.dispatchProgress.timeline
    },
    availableSecondaryRobots() {
      return (this.subcontractCandidates.secondaryRobots || [])
        .filter(robot => Number(robot.id) !== Number(this.acceptancePlan.primaryRobotId))
    },
    distanceText() {
      if (this.distanceMeters == null) {
        return '尚未计算'
      }
      if (this.distanceMeters < 1000) {
        return `${Math.round(this.distanceMeters)} 米`
      }
      return `${(this.distanceMeters / 1000).toFixed(2)} 公里`
    },
    locationHint() {
      if (this.locationError) {
        return this.locationError
      }
      if (!this.selectedTask || !this.hasTargetCoordinate(this.selectedTask)) {
        return '当前任务缺少坐标，暂时无法计算距离。'
      }
      if (this.distanceMeters == null) {
        return '点击按钮后，浏览器会请求定位权限。电脑定位精度取决于系统、Wi-Fi 和浏览器环境。'
      }
      return '这是直线距离，可作为接单参考；真实路线距离后续可以再接地图路线规划。'
    }
  },
  created() {
    if (!this.isAdmin) {
      this.status = 'approved'
    } else {
      this.fetchDerasSettings()
    }
    this.fetchTasks()
  },
  methods: {
    changeStatus(value) {
      if (this.status === value) return
      this.status = value
      this.fetchTasks()
    },
    async fetchDerasSettings() {
      try {
        const response = await getDerasSettings()
        if (response.data.code === 200 && response.data.data) {
          this.derasSettings = {
            ...this.derasSettings,
            ...response.data.data
          }
        }
      } catch (error) {
        this.$message.warning(getApiErrorMessage(error, 'DeRAS设置读取失败，已使用页面默认值。'))
      }
    },
    async saveSettings() {
      this.settingsSaving = true
      try {
        const response = await saveDerasSettings(this.derasSettings)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || 'DeRAS设置保存失败')
          return
        }
        this.derasSettings = {
          ...this.derasSettings,
          ...response.data.data
        }
        this.$message.success('DeRAS设置已保存')
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, 'DeRAS设置保存失败，请稍后重试。'))
      } finally {
        this.settingsSaving = false
      }
    },
    async fetchTasks() {
      this.loading = true
      try {
        const params = { robotType: this.robotType || undefined }
        const response = this.isAdmin
          ? await getAdminTaskMarket({ ...params, status: this.status })
          : await getUserTaskMarket(params)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务市场加载失败')
          return
        }
        this.tasks = response.data.data || []
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '任务市场请求失败，请稍后重试。'))
      } finally {
        this.loading = false
      }
    },
    openDetail(task) {
      this.selectedTask = task
      this.dispatchProgress = null
      this.detailVisible = true
      this.resetAcceptancePlan(task)
      this.checkLocationPermission()
      if (!this.isAdmin && Number(task.allowSubcontract) === 1) {
        this.fetchSubcontractCandidates(task)
      }
      if (this.isMyTask(task)) {
        this.fetchDispatchProgress(task)
      }
    },
    closeDetail() {
      this.detailVisible = false
      this.resetDistance()
    },
    resetAcceptancePlan(task) {
      const shuttle = task && task.serviceType === 'SHUTTLE'
      const beginLat = Number(task && task.beginLat)
      const beginLng = Number(task && task.beginLng)
      const endLat = Number(task && (shuttle ? task.endLat : task.serviceLat))
      const endLng = Number(task && (shuttle ? task.endLng : task.serviceLng))
      this.subcontractCandidates = { primaryRobots: [], secondaryRobots: [] }
      this.acceptancePlan = {
        executionMode: 'DIRECT',
        primaryRobotId: null,
        secondaryRobotId: null,
        handoverAddress: shuttle
          ? `${task.beginAddress || '起点'} 至 ${task.endAddress || '终点'} 的交接点`
          : `${task.serviceAddress || '服务地点'}交接点`,
        handoverLat: Number.isFinite(beginLat) && Number.isFinite(endLat) ? Number(((beginLat + endLat) / 2).toFixed(7)) : (Number.isFinite(endLat) ? endLat : null),
        handoverLng: Number.isFinite(beginLng) && Number.isFinite(endLng) ? Number(((beginLng + endLng) / 2).toFixed(7)) : (Number.isFinite(endLng) ? endLng : null),
        primaryRewardPercent: 50
      }
    },
    async fetchSubcontractCandidates(task) {
      if (!task || !this.currentUser || !this.currentUser.id) return
      this.subcontractCandidatesLoading = true
      try {
        const response = await getSubcontractCandidates({ taskId: task.id, userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.warning(response.data.msg || '协作机器人列表加载失败')
          return
        }
        this.subcontractCandidates = response.data.data || { primaryRobots: [], secondaryRobots: [] }
        const firstPrimary = this.subcontractCandidates.primaryRobots && this.subcontractCandidates.primaryRobots[0]
        this.acceptancePlan.primaryRobotId = firstPrimary ? firstPrimary.id : null
        this.ensureSecondaryRobot()
      } catch (error) {
        this.$message.warning(getApiErrorMessage(error, '协作机器人列表加载失败，请稍后重试。'))
      } finally {
        this.subcontractCandidatesLoading = false
      }
    },
    setExecutionMode(mode) {
      this.acceptancePlan.executionMode = mode
      if (mode === 'SUBCONTRACT') this.ensureSecondaryRobot()
    },
    ensureSecondaryRobot() {
      if (!this.availableSecondaryRobots.some(robot => Number(robot.id) === Number(this.acceptancePlan.secondaryRobotId))) {
        this.acceptancePlan.secondaryRobotId = this.availableSecondaryRobots.length ? this.availableSecondaryRobots[0].id : null
      }
    },
    robotOptionLabel(robot) {
      if (!robot) return '未知机器人'
      return `${robot.name || `机器人 #${robot.id}`} · 评分 ${robot.score == null ? '-' : robot.score}${robot.ownedByCurrentUser ? ' · 我的设备' : ''}`
    },
    validateAcceptancePlan() {
      if (Number(this.selectedTask.allowSubcontract) !== 1) return ''
      if (!this.acceptancePlan.primaryRobotId) return '请选择执行第一段的在线机器人。'
      if (this.acceptancePlan.executionMode !== 'SUBCONTRACT') return ''
      if (!this.acceptancePlan.secondaryRobotId) return '请选择执行第二段的协作机器人。'
      if (!String(this.acceptancePlan.handoverAddress || '').trim()) return '请填写交接地点。'
      if (!Number.isFinite(Number(this.acceptancePlan.handoverLat)) || !Number.isFinite(Number(this.acceptancePlan.handoverLng))) return '请填写有效的交接经纬度。'
      if (Number(this.acceptancePlan.primaryRobotId) === Number(this.acceptancePlan.secondaryRobotId)) return '两段必须选择不同机器人。'
      return ''
    },
    async fetchDispatchProgress(task) {
      if (!task || !task.id || !this.currentUser || !this.currentUser.id) return
      this.dispatchProgressLoading = true
      try {
        const response = await getPublishedTaskDispatchProgress(task.id, { userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.warning(response.data.msg || '派单时间线加载失败')
          return
        }
        this.dispatchProgress = response.data.data || null
      } catch (error) {
        this.$message.warning(getApiErrorMessage(error, '派单时间线请求失败，请稍后重试。'))
      } finally {
        this.dispatchProgressLoading = false
      }
    },
    async close(task) {
      await this.$confirm('关闭后该任务会从已通过市场移除，确定继续吗？', '关闭任务', {
        confirmButtonText: '确定关闭',
        cancelButtonText: '取消',
        type: 'warning'
      })
      await this.runAction(() => closeTask(task.id), '任务已关闭')
    },
    async approve(task) {
      await this.$confirm('确认人工通过该任务？通过后会进入任务市场，服务者可以接单。', 'DeRAS人工复核', {
        confirmButtonText: '确认通过',
        cancelButtonText: '取消',
        type: 'warning'
      })
      await this.runAction(() => approveTask(task.id), '任务已人工通过')
      this.closeDetail()
    },
    async reject(task) {
      const { value } = await this.$prompt('请输入驳回原因，用户会在任务列表中看到该说明。', '驳回复核', {
        confirmButtonText: '确认驳回',
        cancelButtonText: '取消',
        inputValue: task.verificationNote || 'DeRAS人工复核未通过'
      })
      await this.runAction(() => rejectTask(task.id, { reason: value || 'DeRAS人工复核未通过' }), '任务已驳回')
      this.closeDetail()
    },
    async acceptTask() {
      if (!this.currentUser || !this.currentUser.id) {
        this.$message.warning('请先登录后再接单')
        return
      }
      if (!this.selectedTask) {
        return
      }
      if (this.isTakenTask(this.selectedTask)) {
        this.$message.warning('该任务已经被接单，等待服务者执行或取消后重新开放')
        return
      }
      const planError = this.validateAcceptancePlan()
      if (planError) {
        this.$message.warning(planError)
        return
      }

      this.accepting = true
      try {
        const response = await acceptMarketTask({
          taskId: this.selectedTask.id,
          userId: this.currentUser.id,
          executionMode: this.acceptancePlan.executionMode,
          primaryRobotId: this.acceptancePlan.primaryRobotId || undefined,
          secondaryRobotId: this.acceptancePlan.executionMode === 'SUBCONTRACT' ? this.acceptancePlan.secondaryRobotId : undefined,
          handoverAddress: this.acceptancePlan.executionMode === 'SUBCONTRACT' ? this.acceptancePlan.handoverAddress : undefined,
          handoverLat: this.acceptancePlan.executionMode === 'SUBCONTRACT' ? this.acceptancePlan.handoverLat : undefined,
          handoverLng: this.acceptancePlan.executionMode === 'SUBCONTRACT' ? this.acceptancePlan.handoverLng : undefined,
          primaryRewardPercent: this.acceptancePlan.executionMode === 'SUBCONTRACT' ? this.acceptancePlan.primaryRewardPercent : undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '接单失败')
          return
        }
        this.$message.success(response.data.data && response.data.data.message ? response.data.data.message : '接单成功')
        this.closeDetail()
        this.fetchTasks()
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '接单请求失败，请稍后重试。'))
      } finally {
        this.accepting = false
      }
    },
    async runAction(request, successText) {
      try {
        const response = await request()
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '操作失败')
          return
        }
        this.$message.success(successText)
        this.fetchTasks()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(getApiErrorMessage(error, '操作失败，请稍后重试。'))
        }
      }
    },
    calculateDistance() {
      if (!navigator.geolocation) {
        this.locationError = '当前浏览器不支持定位功能。'
        return
      }
      if (!this.hasTargetCoordinate(this.selectedTask)) {
        this.locationError = '当前任务缺少坐标，无法计算距离。'
        return
      }

      this.locating = true
      this.locationError = ''
      navigator.geolocation.getCurrentPosition(
        position => {
          const current = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          const target = this.targetCoordinate(this.selectedTask)
          this.userPosition = current
          this.distanceMeters = this.getDistanceMeters(current.lat, current.lng, target.lat, target.lng)
          this.locationPermission = 'granted'
          this.locating = false
        },
        error => {
          this.locationError = this.resolveLocationError(error)
          if (error && error.code === error.PERMISSION_DENIED) this.locationPermission = 'denied'
          this.locating = false
        },
        {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 30000
        }
      )
    },
    async checkLocationPermission() {
      if (!navigator.permissions || !navigator.permissions.query) return
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        this.locationPermission = permission.state
        permission.onchange = () => {
          this.locationPermission = permission.state
        }
      } catch (error) {
        this.locationPermission = 'unknown'
      }
    },
    resetDistance() {
      this.userPosition = null
      this.distanceMeters = null
      this.locationError = ''
      this.locating = false
    },
    targetAddress(task) {
      if (!task) return '未填写'
      if (task.serviceType === 'SHUTTLE') {
        return task.beginAddress || '起点未填写'
      }
      return task.serviceAddress || '地址未填写'
    },
    targetCoordinate(task) {
      if (!task) return null
      if (task.serviceType === 'SHUTTLE') {
        return {
          lat: Number(task.beginLat),
          lng: Number(task.beginLng)
        }
      }
      return {
        lat: Number(task.serviceLat),
        lng: Number(task.serviceLng)
      }
    },
    hasTargetCoordinate(task) {
      const coordinate = this.targetCoordinate(task)
      return Boolean(coordinate && Number.isFinite(coordinate.lat) && Number.isFinite(coordinate.lng))
    },
    getDistanceMeters(lat1, lng1, lat2, lng2) {
      const earthRadius = 6371000
      const toRad = value => (value * Math.PI) / 180
      const dLat = toRad(lat2 - lat1)
      const dLng = toRad(lng2 - lng1)
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },
    resolveLocationError(error) {
      if (error.code === error.PERMISSION_DENIED) {
        return '定位权限被拒绝，请允许浏览器访问位置后重试。'
      }
      if (error.code === error.POSITION_UNAVAILABLE) {
        return '暂时无法获取当前位置，请检查系统定位或网络环境。'
      }
      if (error.code === error.TIMEOUT) {
        return '定位超时，请稍后重试。'
      }
      return '定位失败，请稍后重试。'
    },
    isMyTask(task) {
      return Boolean(this.currentUser && task && Number(task.publisherUserId) === Number(this.currentUser.id))
    },
    isTakenTask(task) {
      return Boolean(task && Number(task.status) === 1)
    },
    publisherSteps(task) {
      const status = task && task.dispatchStatus
      const taken = this.isTakenTask(task) || status === 'ACCEPTED'
      const pending = status === 'PENDING'
      const fallback = status === 'OPEN_GRAB'
      const rejected = status === 'REJECTED'
      const expired = status === 'EXPIRED'
      const searching = status === 'SEARCHING' || rejected || expired || (!taken && !pending && !fallback)
      return [
        { key: 'search', label: '寻找机器人', done: pending || taken || fallback, active: searching, desc: 'DeRAS正在按类型、在线状态、空闲状态、信誉和距离筛选候选。' },
        { key: 'invite', label: '已发邀请', done: taken, active: pending, desc: pending ? '已向候选机器人发出邀请，等待对方确认。' : '候选机器人会收到派单邀请。' },
        { key: 'redispatch', label: '重派/兜底', active: rejected || expired || fallback, warn: rejected || expired || fallback, desc: this.dispatchFallbackHint(task) || '候选拒绝或超时后会自动尝试下一组机器人。' },
        { key: 'accepted', label: '已接单', done: taken, active: taken, desc: taken ? '机器人已锁定任务，订单进入履约流程。' : '等待某台机器人接受后锁单。' }
      ]
    },
    dispatchFallbackHint(task) {
      if (!task) return ''
      if (task.dispatchStatus === 'OPEN_GRAB') {
        return '自动派单暂未找到合适机器人，任务已开放给服务者抢单；建议检查任务类型、服务地点或提高平台补助。'
      }
      if (task.dispatchStatus === 'REJECTED') {
        return '上一台候选机器人已拒绝，DeRAS正在自动尝试下一组候选。'
      }
      if (task.dispatchStatus === 'EXPIRED') {
        return '上一轮候选超时未响应，DeRAS正在重新派单。'
      }
      const count = Number(task.dispatchNoCandidateCount || 0)
      if (count >= 2) {
        return `DeRAS已连续 ${count} 轮未找到可用候选，建议检查是否有同类型机器人在线、是否开启接单、SDK Agent是否在线。`
      }
      return ''
    },
    timelineTone(item) {
      const type = String(item && item.type || '').toUpperCase()
      if (type.includes('EXPIRED') || type.includes('REJECTED') || type.includes('NO_CANDIDATE')) return 'warn'
      if (type.includes('OPEN_GRAB')) return 'fallback'
      if (type.includes('ACCEPT')) return 'done'
      return 'active'
    },
    timelineTitle(item) {
      const title = item && item.title ? String(item.title) : ''
      const map = {
        'Task published': '任务已发布',
        'Dispatch scan': 'DeRAS 扫描候选',
        'Dispatch invitation sent': '已发出派单邀请',
        'Dispatch invitation expired': '候选超时',
        'Dispatch fallback opened': '开放抢单兜底',
        'Candidate robot accepted dispatch': '机器人已接受派单'
      }
      return map[title] || title || '派单事件'
    },
    timelineContent(item) {
      const content = item && item.content ? String(item.content) : ''
      if (!content) return 'DeRAS已记录该派单事件。'
      return content
        .replace('Task entered DeRAS dispatch pipeline.', '任务进入 DeRAS 自动派单流程。')
        .replace('Auto dispatch could not find a candidate, so the task is visible in the task market.', '自动派单暂未找到候选，任务已在任务市场开放兜底。')
    },
    formatTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    dispatchStatusText(task) {
      if (!task) return '等待派单'
      if (this.isTakenTask(task)) return '已被接单'
      if (task.dispatchStatus === 'SEARCHING') return 'DeRAS正在寻找机器人'
      if (task.dispatchStatus === 'PENDING') return '等待机器人确认'
      if (task.dispatchStatus === 'OPEN_GRAB') return '暂无候选，开放抢单'
      if (task.dispatchStatus === 'REJECTED') return '候选拒绝，正在重派'
      if (task.dispatchStatus === 'EXPIRED') return '候选超时，正在重派'
      return task.dispatchStatusText || 'DeRAS正在寻找机器人'
    },
    dispatchSubText(task) {
      if (!task) return '等待 DeRAS 同步派单状态'
      if (this.isTakenTask(task)) {
        return task.dispatchRobotName ? `机器人「${task.dispatchRobotName}」已锁定该任务` : '任务已经进入履约流程'
      }
      if (task.dispatchStatus === 'PENDING') {
        const name = task.dispatchRobotName || '候选机器人'
        return `已并发邀请候选机器人，当前最新候选为 ${name}，谁先确认谁锁定任务`
      }
      if (task.dispatchStatus === 'SEARCHING') {
        const count = Number(task.dispatchNoCandidateCount || 0)
        return count > 0
          ? `已扫描 ${count} 轮，DeRAS会继续寻找在线、空闲、类型匹配的机器人`
          : '系统正在按类型、在线状态、空闲状态和信誉分筛选候选机器人'
      }
      if (task.dispatchStatus === 'OPEN_GRAB') {
        return task.dispatchFallbackAt ? `自动派单暂未找到候选，已于 ${task.dispatchFallbackAt} 开放给服务者抢单` : '自动派单暂未找到候选，当前开放给服务者抢单'
      }
      if (task.dispatchStatus === 'REJECTED') return '上一台候选机器人已拒绝，系统正在尝试下一台'
      if (task.dispatchStatus === 'EXPIRED') return '上一台候选机器人超时未响应，系统正在尝试下一台'
      if (task.dispatchCandidateCount > 0) return '暂无新候选，任务仍开放给服务者抢单'
      return '类型匹配、在线状态、空闲状态和信誉分会参与推荐'
    },
    dispatchBannerClass(task) {
      const status = task && task.dispatchStatus
      return {
        searching: status === 'SEARCHING',
        pending: status === 'PENDING',
        accepted: this.isTakenTask(task) || status === 'ACCEPTED',
        open: ['OPEN_GRAB', 'REJECTED', 'EXPIRED', 'CANCELLED'].includes(status)
      }
    },
    robotTypeLabel(value) {
      const matched = this.robotTypes.find(item => item.value === value)
      return matched ? matched.label : (value || '未分类')
    },
    serviceTypeLabel(value) {
      return value === 'SHUTTLE' ? '接驳服务' : '到场服务'
    },
    formatCoin(value) {
      const numberValue = Number(value || 0)
      if (!Number.isFinite(numberValue)) {
        return '0.00'
      }
      return numberValue.toFixed(2)
    },
    platformFee(task) {
      return Number(task && task.estimatedRobocoin ? task.estimatedRobocoin : 0) * 0.01
    },
    providerIncome(task) {
      const amount = Number(task && task.estimatedRobocoin ? task.estimatedRobocoin : 0)
      return Math.max(0, amount - this.platformFee(task))
    },
    taskStatusLabel(task) {
      if (this.isTakenTask(task)) return '已被接单'
      if (task.verificationStatus === 'PASSED') return '已通过'
      if (task.verificationStatus === 'REJECTED') return '未通过'
      return '审核中'
    },
    formatAddress(task) {
      if (task.serviceType === 'SHUTTLE') {
        return `${task.beginAddress || '起点未填'} → ${task.endAddress || '终点未填'}`
      }
      return task.serviceAddress || '地址未填写'
    }
  }
}
</script>

<style scoped>
.market-page {
  position: relative;
  min-height: 100vh;
  padding: 34px;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 12%, rgba(255, 183, 82, 0.18), transparent 28%),
    radial-gradient(circle at 88% 8%, rgba(79, 198, 255, 0.17), transparent 24%),
    linear-gradient(135deg, #06111e 0%, #0a1524 48%, #050b15 100%);
  color: #fff6ea;
}

.market-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 48px 48px;
  opacity: 0.45;
}

.market-header,
.toolbar,
.task-grid {
  position: relative;
  z-index: 1;
}

.market-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 10px;
  color: rgba(255, 205, 145, 0.86);
  letter-spacing: 0.35em;
  font-size: 12px;
}

.market-header h1 {
  margin: 0;
  font-size: clamp(36px, 5vw, 68px);
  font-family: ui-serif, serif;
}

.market-header p {
  margin: 12px 0 0;
  color: rgba(255, 246, 234, 0.7);
}

.header-actions {
  display: flex;
  gap: 12px;
}

.ghost-btn,
.primary-btn,
.mini-btn {
  border: none;
  border-radius: 999px;
  cursor: pointer;
}

.ghost-btn {
  padding: 11px 16px;
  color: #fff6ea;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
}

.primary-btn {
  padding: 11px 18px;
  color: #07121d;
  font-weight: 700;
  background: linear-gradient(135deg, #d9a657, #5fa7cc);
}

.glass-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(8, 16, 28, 0.72);
  backdrop-filter: blur(18px);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  padding: 16px 18px;
  border-radius: 24px;
}

.deras-panel {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 22px;
  padding: 20px 22px;
  border-radius: 24px;
}

.deras-panel h2 {
  margin: 0;
  font-size: 24px;
}

.deras-panel p {
  margin: 8px 0 0;
  max-width: 720px;
  color: rgba(255, 246, 234, 0.72);
  line-height: 1.7;
}

.deras-controls {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.subcontract-planner {
  margin-top: 18px;
  padding: 18px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.planner-heading,
.mode-switch {
  display: flex;
  align-items: center;
}

.planner-heading {
  justify-content: space-between;
  gap: 16px;
}

.planner-heading span,
.planner-fields label > span {
  display: block;
  margin-bottom: 7px;
  color: rgba(255, 205, 145, 0.86);
  font-size: 12px;
}

.planner-heading strong {
  font-size: 20px;
}

.mode-switch {
  gap: 4px;
  padding: 4px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.mode-switch button {
  height: 36px;
  padding: 0 14px;
  color: rgba(255, 246, 234, 0.72);
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.mode-switch button.active {
  color: #07121d;
  background: #8ed7ff;
}

.planner-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;
}

.planner-fields label,
.planner-fields :deep(.el-select) {
  width: 100%;
}

.coordinate-input {
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  color: #eef4ff;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  outline: none;
  background: rgba(5, 12, 21, 0.76);
}

.coordinate-input:focus {
  border-color: #8ed7ff;
}

.wide-field {
  grid-column: 1 / -1;
}

.reward-field {
  padding: 4px 8px 0;
}

.reward-slider {
  width: 100%;
  height: 24px;
  accent-color: #8ed7ff;
  cursor: pointer;
}

.planner-note {
  margin: 14px 0 0;
  color: rgba(255, 246, 234, 0.66);
  line-height: 1.7;
}

.deras-controls label {
  display: grid;
  gap: 8px;
  color: rgba(255, 205, 145, 0.9);
  font-size: 12px;
}

.switch-row {
  min-height: 58px;
  align-content: center;
}

.status-tabs {
  flex: 1;
  display: flex;
  gap: 8px;
  padding: 6px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.05);
}

.status-tabs button {
  height: 38px;
  min-width: 96px;
  padding: 0 16px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: rgba(255, 246, 234, 0.72);
  cursor: pointer;
  font-weight: 700;
}

.status-tabs button.active {
  color: #06111e;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.task-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 18px;
  min-height: 260px;
}

.task-card {
  position: relative;
  overflow: hidden;
  padding: 22px;
  border-radius: 26px;
  transition: opacity 0.2s ease, filter 0.2s ease, transform 0.2s ease;
}

.task-card-taken {
  opacity: 0.68;
  filter: grayscale(0.28);
}

.task-card-taken::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    linear-gradient(135deg, rgba(5, 13, 23, 0.1), rgba(5, 13, 23, 0.42)),
    repeating-linear-gradient(
      -35deg,
      rgba(255, 255, 255, 0.03) 0,
      rgba(255, 255, 255, 0.03) 8px,
      transparent 8px,
      transparent 18px
    );
}

.task-card-taken > *:not(.taken-stamp) {
  position: relative;
  z-index: 0;
}

.taken-stamp {
  position: absolute;
  top: 22px;
  right: 18px;
  z-index: 3;
  padding: 8px 14px;
  color: #ffdfbb;
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 2px;
  border: 2px solid rgba(255, 184, 112, 0.86);
  border-radius: 10px;
  background: rgba(5, 13, 23, 0.64);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28);
  transform: rotate(-12deg);
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.pill-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.type-pill,
.mine-pill,
.status-pill {
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.type-pill {
  color: #06111e;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.mine-pill {
  color: #06111e;
  font-weight: 700;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.status-pill {
  color: #ffe2b8;
  background: rgba(255, 196, 122, 0.14);
}

.task-card h2 {
  margin: 18px 0 10px;
  font-size: 24px;
}

.dispatch-banner {
  margin-top: 14px;
  padding: 12px 14px;
  display: grid;
  gap: 4px;
  border-radius: 18px;
  background: rgba(142, 215, 255, 0.08);
  border: 1px solid rgba(142, 215, 255, 0.18);
}

.dispatch-banner strong {
  font-size: 14px;
  color: #e9f6ff;
}

.dispatch-banner span {
  font-size: 12px;
  line-height: 1.55;
  color: rgba(235, 244, 255, 0.72);
}

.dispatch-banner em {
  color: rgba(143, 241, 199, 0.78);
  font-size: 12px;
  font-style: normal;
  line-height: 1.55;
}

.dispatch-banner.pending {
  background: rgba(255, 208, 138, 0.1);
  border-color: rgba(255, 208, 138, 0.26);
}

.dispatch-banner.searching {
  background: rgba(142, 215, 255, 0.1);
  border-color: rgba(142, 215, 255, 0.28);
}

.dispatch-banner.accepted {
  background: rgba(143, 241, 199, 0.1);
  border-color: rgba(143, 241, 199, 0.28);
}

.dispatch-banner.open {
  background: rgba(255, 139, 125, 0.08);
  border-color: rgba(255, 139, 125, 0.22);
}

.publisher-progress {
  margin-top: 12px;
  padding: 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.publisher-progress-head,
.timeline-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.publisher-progress-head strong,
.timeline-head strong {
  color: #fff6ea;
}

.publisher-progress-head button,
.timeline-head button {
  border: none;
  border-radius: 999px;
  padding: 6px 10px;
  color: #06111e;
  font-weight: 800;
  cursor: pointer;
  background: #8ed7ff;
}

.publisher-progress-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.publisher-progress-steps span {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: rgba(255, 246, 234, 0.58);
  font-size: 12px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.06);
}

.publisher-progress-steps span.active {
  color: #07121d;
  background: #ffd08a;
}

.publisher-progress-steps span.done {
  color: #07121d;
  background: #8ff1c7;
}

.publisher-progress-steps span.warn {
  color: #fff;
  background: rgba(255, 139, 125, 0.55);
}

.fallback-hint {
  margin: 10px 0 0;
  color: #ffd7c9;
  line-height: 1.6;
  font-size: 12px;
}

.desc {
  min-height: 52px;
  color: rgba(255, 246, 234, 0.68);
  line-height: 1.7;
}

.meta-list {
  display: grid;
  gap: 8px;
  margin-top: 16px;
  color: rgba(223, 243, 255, 0.76);
  font-size: 13px;
}

.card-actions,
.dialog-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.mini-btn {
  padding: 9px 13px;
  color: #06111e;
  font-weight: 700;
}

.info {
  background: #8ed7ff;
}

.approve {
  background: #8ff1c7;
}

.danger {
  background: #ff8b7d;
}

.locked {
  color: rgba(255, 246, 234, 0.78);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  cursor: not-allowed;
}

.mini-btn:disabled,
.primary-btn:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

.market-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: grid;
  place-items: center;
  padding: 28px;
  background: rgba(1, 7, 13, 0.74);
  backdrop-filter: blur(10px);
}

.market-modal-panel {
  position: relative;
  width: min(760px, 100%);
  height: min(88vh, 880px);
  max-height: calc(100vh - 56px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 28px 30px 30px;
  border-radius: 8px;
  color: #fff6ea;
  background:
    radial-gradient(circle at 18% 0%, rgba(95, 167, 204, 0.18), transparent 34%),
    linear-gradient(145deg, #102238 0%, #071322 48%, #050b15 100%);
  border: 1px solid rgba(142, 215, 255, 0.18);
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.58);
}

.modal-close-btn {
  position: absolute;
  top: 16px;
  right: 18px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid rgba(142, 215, 255, 0.18);
  color: #fff6ea;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
}

.detail-panel {
  color: #fff6ea;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 6px 0 0;
}

.detail-panel .eyebrow {
  color: #ffd69f;
  text-shadow: 0 0 18px rgba(255, 214, 159, 0.2);
}

.detail-panel h2 {
  margin: 0;
  font-size: 30px;
  color: #fff8ee;
  overflow-wrap: anywhere;
  text-shadow: 0 8px 24px rgba(0, 0, 0, 0.42);
}

.detail-desc {
  color: rgba(255, 246, 234, 0.86);
  line-height: 1.8;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
}

.detail-grid div,
.distance-box {
  padding: 14px;
  border-radius: 18px;
  background: rgba(5, 13, 23, 0.72);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.detail-grid span,
.distance-box span {
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 205, 145, 0.86);
  font-size: 12px;
}

.detail-grid strong,
.distance-box strong {
  color: #fff6ea;
  line-height: 1.6;
}

.publisher-timeline {
  margin: 18px 0;
  padding: 16px;
  border-radius: 18px;
  background: rgba(5, 13, 23, 0.72);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.timeline-summary {
  display: grid;
  gap: 5px;
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(142, 215, 255, 0.08);
}

.timeline-summary span {
  color: #e9f6ff;
  font-weight: 800;
}

.timeline-summary em {
  color: rgba(143, 241, 199, 0.78);
  font-style: normal;
  font-size: 12px;
}

.timeline-row {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 10px;
  margin-top: 14px;
}

.timeline-row i {
  width: 10px;
  height: 10px;
  margin-top: 7px;
  border-radius: 999px;
  background: #8ed7ff;
  box-shadow: 0 0 0 5px rgba(142, 215, 255, 0.12);
}

.timeline-row i.done {
  background: #8ff1c7;
  box-shadow: 0 0 0 5px rgba(143, 241, 199, 0.12);
}

.timeline-row i.warn,
.timeline-row i.fallback {
  background: #ff8b7d;
  box-shadow: 0 0 0 5px rgba(255, 139, 125, 0.12);
}

.timeline-row strong {
  color: #fff6ea;
}

.timeline-row p {
  margin: 5px 0;
  color: rgba(255, 246, 234, 0.72);
  line-height: 1.6;
}

.timeline-row small {
  color: rgba(255, 205, 145, 0.72);
}

.distance-box {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
}

.distance-box p {
  margin: 8px 0 0;
  color: rgba(255, 246, 234, 0.78);
  line-height: 1.7;
}

.location-permission-notice {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px 18px;
  align-items: center;
  margin-top: 12px;
  padding: 14px;
  border: 1px solid rgba(255, 208, 138, 0.28);
  border-radius: 8px;
  color: #fff6ea;
  background: rgba(255, 208, 138, 0.09);
}

.location-permission-notice strong,
.location-permission-notice span {
  grid-column: 1;
}

.location-permission-notice span {
  color: rgba(255, 246, 234, 0.72);
  line-height: 1.5;
}

.location-permission-notice button {
  grid-column: 2;
  grid-row: 1 / span 2;
  padding: 10px 14px;
  border: 0;
  border-radius: 7px;
  color: #06111e;
  background: #8ed7ff;
  cursor: pointer;
  font-weight: 800;
}

.dialog-actions {
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: 14px 0 4px;
  background: linear-gradient(180deg, transparent, #0b1726 24%);
}

.locate-btn {
  white-space: nowrap;
}

@media (max-width: 760px) {
  .market-page {
    padding: 18px;
  }

  .market-header,
  .toolbar,
  .deras-panel,
  .distance-box {
    align-items: stretch;
    flex-direction: column;
  }

  .deras-controls {
    justify-content: flex-start;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .market-modal-backdrop {
    padding: 10px;
  }

  .market-modal-panel {
    width: 100%;
    height: calc(100vh - 20px);
    padding: 22px 16px 24px;
  }

  .location-permission-notice {
    grid-template-columns: 1fr;
  }

  .location-permission-notice button {
    grid-column: 1;
    grid-row: auto;
  }

  .planner-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .mode-switch,
  .mode-switch button {
    flex: 1;
  }

  .planner-fields {
    grid-template-columns: minmax(0, 1fr);
  }

  .planner-fields label,
  .wide-field {
    min-width: 0;
    grid-column: 1;
  }

  .planner-fields :deep(.el-select__wrapper) {
    min-width: 0;
  }
}
</style>
